// ─────────────────────────────────────────────────────────────────────────────
// Shared profit-bonus engine
//
// Single source of truth for how a closed job turns into per-tech bonus rows
// (one "lead" row + N "crew" rows). Both the CrewPerformance leaderboard and
// the BonusTracker consume this so the two surfaces can never drift apart.
//
// This is a faithful extraction of the logic that previously lived inline in
// CrewPerformance's `bonusRows` useMemo. Invoice/payment status is injected by
// the caller (`getStatus`) because the two views derive "is this job paid"
// from different places (CrewPerformance: the invoices table; BonusTracker:
// the jb_jobs rollup columns).
// ─────────────────────────────────────────────────────────────────────────────

export const PROFIT_BONUS_START = new Date(2026, 0, 1); // floor — no jobs older than this
export const DEFAULT_LEAD_PCT   = 10;
export const DEFAULT_CREW_PCT   = 5;
export const DEFAULT_REV_MIN    = 5500;
export const DEFAULT_MARGIN_MIN = 50;

// Roles eligible to be a job's lead — used for explicit lead assignment
// (jb_jobs.lead_tech custom field) and the top-hours auto-fallback.
export const LEAD_ROLES = ["journeyman", "ops lead tech", "ops lead"];

// Techs never auto-assigned as a job's LEAD regardless of their Jobber role —
// they still earn crew shares like anyone else. Dylan 2026-07-23: "Lorne is
// just crew for now" (his Jobber role is journeyman, which is lead-eligible).
export const NOT_LEAD_FIRST = ["lorne"];
// Hardcoded carve-outs: never receive job-profitability bonuses regardless of
// role. Taylor (admin) is excluded here — admins get a separate bonus
// structure rather than a per-job lead/crew share.
export const BONUS_EXCLUDED_FIRST = ["dylan", "colton", "cindy", "taylor"];

export function firstName(name) {
  if (!name) return "";
  return name.trim().split(/\s+/)[0].toLowerCase();
}

export function isBonusExcluded(first) {
  return BONUS_EXCLUDED_FIRST.includes((first || "").toLowerCase());
}

// Profit above the qualifying margin floor (defaults to 50% if unset).
export function calcExcess(rev, profitAmt, marginMin) {
  const r  = Number(rev) || 0;
  const pa = Number(profitAmt) || 0;
  return pa - r * (Number(marginMin) / 100 || 0.5);
}

export function jobQualifies(rev, profitPct, revMin, marginMin) {
  return Number(rev) > Number(revMin) && Number(profitPct) > Number(marginMin);
}

// ── Paperwork accountability gate (same-day notes + same-day visit close) ────
// Being *assigned* to a visit used to be enough to owe a note and the close.
// That punished whoever was on the visit's roster rather than whoever actually
// did the work — Tommy reps the crew on a site visit, the guys who worked the
// day are the ones who close it and write it up, but the deduction landed on
// him. A tech is only on the hook when they actually worked the job that day
// AND their hours are a meaningful share of the crew's that day.
//
// Dylan 2026-08-04: "make sure the guys actually worked that day."
// ─────────────────────────────────────────────────────────────────────────────

// Visits scheduled before this date keep the old always-on-the-hook rule, so
// months whose bonuses already went out don't retroactively change. July 2026
// was sent to the crew on 2026-08-04 — the day this gate was written.
export const PAPERWORK_GATE_START = new Date(2026, 7, 1); // 2026-08-01 local
// Share of the day's top worker's hours you must log to own the paperwork.
export const PAPERWORK_MIN_SHARE = 0.5;

// Local calendar day key. Must match the `${y}-${m}-${d}` shape the metric
// code already uses for dedup keys so the two never disagree on a day boundary.
export function dayKeyOf(dateLike) {
  if (!dateLike) return null;
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  if (isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

// Index of hours worked per (job, tech, calendar day), plus the day's top
// worker on each job — that's the denominator for the "meaningful share" test.
export function buildPaperworkHoursIndex(timesheets = []) {
  const byTech = new Map();     // `${jobNum}|${techFirst}|${day}` → hours
  const topByJobDay = new Map();// `${jobNum}|${day}`              → max hours by any one tech
  timesheets.forEach(ts => {
    const jn = String(ts.job_number || "").trim();
    const f = firstName(ts.user_name || "");
    const day = dayKeyOf(ts.start_at);
    if (!jn || !f || !day) return;
    const k = `${jn}|${f}|${day}`;
    const total = (byTech.get(k) || 0) + (Number(ts.duration_hours) || 0);
    byTech.set(k, total);
    const jk = `${jn}|${day}`;
    if (total > (topByJobDay.get(jk) || 0)) topByJobDay.set(jk, total);
  });
  return { byTech, topByJobDay };
}

// True when this tech owes the paperwork for this job on this day.
// Fails OPEN (returns true, i.e. old behaviour) whenever we can't judge —
// missing date, missing job number, or no index — so a data gap never silently
// erases someone's accountability.
export function owesPaperwork({ jobNumber, techFirst, date, hoursIndex, minShare = PAPERWORK_MIN_SHARE }) {
  const day = dayKeyOf(date);
  if (!day) return true;
  const d = date instanceof Date ? date : new Date(date);
  if (d < PAPERWORK_GATE_START) return true;   // grandfathered — already-paid months
  const jn = String(jobNumber || "").trim();
  if (!jn || !hoursIndex || !techFirst) return true;

  const mine = hoursIndex.byTech.get(`${jn}|${techFirst}|${day}`) || 0;
  if (mine <= 0) return false;                 // never clocked in — not their paperwork
  const top = hoursIndex.topByJobDay.get(`${jn}|${day}`) || 0;
  if (top <= 0) return true;
  return mine >= top * minShare;               // brief rep stop alongside a full crew → exempt
}

// jobNum (string) → Map<techFirst, totalHours>, built from jobber_timesheets.
export function buildJobTechHoursMap(timesheets = []) {
  const map = new Map();
  timesheets.forEach(ts => {
    const jn = String(ts.job_number || "").trim();
    if (!jn || !ts.user_name) return;
    const f = firstName(ts.user_name);
    if (!f) return;
    const inner = map.get(jn) || new Map();
    inner.set(f, (inner.get(f) || 0) + (Number(ts.duration_hours) || 0));
    map.set(jn, inner);
  });
  return map;
}

// techFirst (lowercase) → role (lowercase), built from jobber_users.
export function buildRoleByFirst(jobberUsers = []) {
  const out = new Map();
  jobberUsers.forEach(u => {
    const first = firstName(u.name || "");
    if (!first || !u.role || u.is_active === false) return;
    out.set(first, String(u.role).toLowerCase().trim());
  });
  return out;
}

function parseTs(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Build one row per (job, tech, role) for every closed job since the floor —
 * NOT just bonus-qualifying jobs. `qualifies` drives whether a row carries a
 * real $ bonus. Callers filter to qualifying/paying rows as needed.
 *
 * @param {object[]} jobs          jb_jobs rows. May carry `_closedDate` / `_leadTech`
 *                                 (CrewPerformance precomputes these); falls back to
 *                                 closed_date / lead_tech otherwise.
 * @param {Map} jobTechHoursMap    from buildJobTechHoursMap
 * @param {Map} roleByFirst        from buildRoleByFirst
 * @param {object} overrides       map keyed `${jobNum}|${techFirst}` → override row
 * @param {object} settings        { lead_pct, crew_pct, revenue_threshold, margin_threshold }
 * @param {function} [getStatus]   (jobNum, job) → { status, invoiced, outstanding, paid }
 * @param {function} [canonicalName] (firstName) → display name
 * @param {Iterable<string>} [excludedJobs] job_nums manually removed from bonuses
 *                                 (and the leaderboard). Such jobs yield no rows.
 */
export function computeBonusRows({
  jobs = [],
  jobTechHoursMap = new Map(),
  roleByFirst = new Map(),
  overrides = {},
  settings = {},
  getStatus,
  canonicalName,
  excludedJobs,
}) {
  const excludedSet = excludedJobs instanceof Set
    ? excludedJobs
    : new Set(Array.from(excludedJobs || [], v => String(v)));
  const lead_pct          = settings.lead_pct          ?? DEFAULT_LEAD_PCT;
  const crew_pct          = settings.crew_pct          ?? DEFAULT_CREW_PCT;
  const revenue_threshold = settings.revenue_threshold ?? DEFAULT_REV_MIN;
  const margin_threshold  = settings.margin_threshold  ?? DEFAULT_MARGIN_MIN;
  const nameOf = typeof canonicalName === "function"
    ? canonicalName
    : (f) => (f ? f[0].toUpperCase() + f.slice(1) : "");

  const rows = [];
  jobs.forEach(j => {
    const jobNum = String(j.job_num || "");
    if (excludedSet.has(jobNum)) return; // manually removed from bonuses + leaderboard

    // Deleted jobs must never generate a bonus — a deleted, un-invoiced job (e.g.
    // #2432 Elisha Stebbings) was surfacing a spurious ~$149 payable bonus.
    // Check BOTH columns. handleJobEvent writes the pair, but refreshJobRevenue
    // rewrites derived_status from payment state, so a deleted job whose
    // derived_status got stomped would slip through a derived_status-only test.
    // Matches isDeadJob() in src/lib/referralPayout.js.
    if (String(j.derived_status || "").toLowerCase() === "deleted") return;
    if (String(j.status || "").toLowerCase() === "deleted") return;

    const closed = j._closedDate || parseTs(j.closed_date);
    if (!closed || closed < PROFIT_BONUS_START) return;
    if (!(j.total_revenue > 0)) return;
    if (j.profit_pct == null) return;

    const qualifies = jobQualifies(j.total_revenue, j.profit_pct, revenue_threshold, margin_threshold);
    const excess = qualifies ? Math.max(0, calcExcess(j.total_revenue, j.profit, margin_threshold)) : 0;
    const crewPool = excess * (crew_pct / 100);

    const techHours = jobTechHoursMap.get(jobNum) || new Map();

    // Eligible tech first-names: have a Jobber role, not in excluded list.
    const eligible = [];
    techHours.forEach((hrs, f) => {
      if (isBonusExcluded(f)) return;
      if (!roleByFirst.has(f)) return; // no role = not bonus-eligible
      eligible.push({ first: f, hours: hrs, role: roleByFirst.get(f) });
    });

    // Pick lead: honor jb_jobs.lead_tech custom field (even with no clocked
    // hours) if that tech is roled + lead-eligible; else top-hours lead.
    let leadFirst = null;
    const namedLead = (j._leadTech != null ? j._leadTech : j.lead_tech);
    const leadCustomFirst = namedLead ? firstName(namedLead) : null;
    const isLeadEligible = (first) => {
      if (!first || isBonusExcluded(first) || NOT_LEAD_FIRST.includes(first)) return false;
      const role = roleByFirst.get(first);
      return !!role && LEAD_ROLES.includes(role);
    };
    if (isLeadEligible(leadCustomFirst)) {
      leadFirst = leadCustomFirst;
    } else {
      const leadCandidates = eligible
        .filter(e => LEAD_ROLES.includes(e.role) && !NOT_LEAD_FIRST.includes(e.first))
        .sort((a, b) => b.hours - a.hours);
      if (leadCandidates.length) leadFirst = leadCandidates[0].first;
    }

    const status = getStatus
      ? getStatus(jobNum, j)
      : { status: "completed", invoiced: 0, outstanding: 0, paid: 0 };

    // Lead row — skipped entirely if this tech is excluded from this job.
    if (leadFirst) {
      const ov = overrides[`${jobNum}|${leadFirst}`];
      if (!ov?.excluded) {
        const pct = ov?.bonus_pct != null ? Number(ov.bonus_pct) : lead_pct;
        const manual = ov?.manual_amount != null;
        // A manual amount REPLACES the computed bonus (and applies even when the
        // job doesn't qualify). Negative = a deduction.
        const bonus = manual ? Number(ov.manual_amount) : (qualifies ? excess * (pct / 100) : 0);
        rows.push({
          jobNum, jobId: j.id, job: j,
          techFirst: leadFirst,
          techName:  nameOf(leadFirst),
          role:      "lead",
          excess,
          bonus_pct: pct,
          bonus,
          weight: null,
          qualifies,
          manual,
          manualAmount: manual ? Number(ov.manual_amount) : null,
          notes: ov?.notes ?? null,
          paid:      !!ov?.paid,
          paid_amount: ov?.paid_amount ?? null,
          paidMonth: ov?.paid_month ?? null, // batch month that paid it (pins the view)
          payAnyway: !!ov?.pay_anyway,
          overridden: ov?.bonus_pct != null,
          status: status.status,
          invoiced:    status.invoiced,
          outstanding: status.outstanding,
          paidOnInvoice: status.paid,
          closedDate: closed,
          jobUpdatedAt: parseTs(j.updated_at),
          clientName: j.client_name,
          jobTitle:   j.title,
          revenue:    Number(j.total_revenue) || 0,
          profit:     Number(j.profit) || 0,
          profitPct:  Number(j.profit_pct),
        });
      }
    }

    // Crew rows: everyone else with a role who logged hours (excludes carve-outs
    // like admins via isBonusExcluded in the `eligible` filter above). A tech
    // excluded from this job drops out of the split, so the pool redistributes
    // among the remaining members.
    const crewSet = new Set(eligible.filter(e => e.first !== leadFirst).map(e => e.first));
    const crewArr = [...crewSet].filter(f => !overrides[`${jobNum}|${f}`]?.excluded);
    if (crewArr.length === 0) return;

    // Read crew weights from overrides (NULL = equal split)
    const crewWeights = crewArr.map(f => {
      const ov = overrides[`${jobNum}|${f}`];
      return { first: f, ov, w: ov?.weight != null ? Number(ov.weight) : null };
    });
    const explicitSum = crewWeights.reduce((s, x) => s + (x.w != null ? x.w : 0), 0);
    const equalCount  = crewWeights.filter(x => x.w == null).length;
    const remainder   = Math.max(0, 1 - explicitSum);
    const perEqual    = equalCount > 0 ? remainder / equalCount : 0;

    crewWeights.forEach(x => {
      const eff = x.w != null ? x.w : perEqual;
      const manual = x.ov?.manual_amount != null;
      // A manual amount REPLACES the computed share (applies even when the job
      // doesn't qualify). Negative = a deduction.
      const bonus = manual ? Number(x.ov.manual_amount) : (qualifies ? crewPool * eff : 0);
      rows.push({
        jobNum, jobId: j.id, job: j,
        techFirst: x.first,
        techName:  nameOf(x.first),
        role:      "crew",
        excess,
        bonus_pct: crew_pct, // pool pct (not per-member)
        bonus,
        weight: eff,
        qualifies,
        manual,
        manualAmount: manual ? Number(x.ov.manual_amount) : null,
        notes: x.ov?.notes ?? null,
        weightOverridden: x.ov?.weight != null,
        paid:      !!x.ov?.paid,
        paid_amount: x.ov?.paid_amount ?? null,
        paidMonth: x.ov?.paid_month ?? null, // batch month that paid it (pins the view)
        payAnyway: !!x.ov?.pay_anyway,
        overridden: x.ov?.bonus_pct != null,
        status: status.status,
        invoiced:    status.invoiced,
        outstanding: status.outstanding,
        paidOnInvoice: status.paid,
        closedDate: closed,
        jobUpdatedAt: parseTs(j.updated_at),
        clientName: j.client_name,
        jobTitle:   j.title,
        revenue:    Number(j.total_revenue) || 0,
        profit:     Number(j.profit) || 0,
        profitPct:  Number(j.profit_pct),
        crewSize:   crewArr.length,
      });
    });
  });
  return rows;
}
